import type { ActivatedRoute } from '@angular/router';
import {
  loadBoolFilterFromGetParams,
  loadFloatFilterFromGetParams,
  loadIntFilterFromGetParams,
  loadStringFilterFromGetParams,
} from '../reporting-core/utils';
import {
  InvoiceStatus, LineItemPurchaseType,
  PaymentMethodType,
  PaymentStatus,
  PaymentType,
  PaymentTypeMixed,
  PaymentTypeWithMixed,
} from './payment-enums';

export interface PaymentLineItem {
  id: number;
  purchase_type: LineItemPurchaseType;
  description: string;
  amount: number;
  booking_id: number | null;
}

export interface Payment {
  id: number;
  url: string;
  status: PaymentStatus;
  failure_message: string;
  amount: number;
  amount_pre_tax: number;
  tax_amount: number;
  amount_refunded: number;
  amount_requested?: number | null;
  pending_refund_amount?: number | null;
  payment_type: PaymentType;
  payment_method_type: PaymentMethodType;
  credit_card_last4: string;
  credit_card_brand: string;
  bank_account_last4: string;
  bank_account_bank_name: string;
  created_date: string;
  invoice_id: number;
  invoice_description: string;
  customer_id?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_birthday?: string;
  staff_user_id?: number;
  staff_user_first_name?: string;
  staff_user_last_name?: string;
  staff_user_email?: string;
  line_items: PaymentLineItem[];
  discount_amount: number;
  discount_code: string;
  discount_name: string;
  discount_display_name: string;
  revenue_account_codes: {[key: string]: number};
  location_id: number | null;
  location_name: string | null;
  stripe_payment_intent_id: string;
  gravity_external_transaction_id: string;
  gravity_unique_transaction_id: string;
  mx_merchant_payment_link_name: string;
  mx_merchant_payment_link_id: string;
  mx_merchant_payment_id: string;
  gift_card_number: string;
  created_at: string;
  updated_at: string;
}

export interface RefundLog {
  id: number;
  url: string;
  amount: number;
  payment_id: number;
  payment_url: string;
  payment_type: PaymentType;
  payment_method_type: PaymentMethodType;
  credit_card_last4: string;
  credit_card_brand: string;
  bank_account_last4: string;
  bank_account_bank_name: string;
  created_date: string;
  invoice_id: number;
  invoice_description: string;
  customer_id?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_birthday?: string;
  revenue_account_codes: {[key: string]: number};
  location_id: number | null;
  location_name: string | null;
  stripe_refund_id: string;
  gravity_refund_external_transaction_id: string;
  created_at: string;
  updated_at: string;
}

export interface DiscountUsageLog {
  id: number;
  url: string;
  membership_discount_id: number | null;
  membership_discount_name: string | null;
  membership_discount_code: string | null;
  entry_pass_discount_id: number | null;
  entry_pass_discount_name: string | null;
  entry_pass_discount_code: string | null;
  event_discount_id: number | null;
  event_discount_name: string | null;
  event_discount_code: string | null;
  invoice_id: number;
  invoice_description: string;
  location_id: number | null;
  location_name: string | null;
  customer_id: number | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  customer_birthday: string | null;
  is_complete: boolean;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: number;
  status: InvoiceStatus;
  amount_due: number;
  payment_processor_connection_id: number | null;
  customer_available_credit: number | null;
}

export interface AccountCreditPaymentResult {
  amount_applied: number;
  invoice_amount_due: number;
  invoice_status: InvoiceStatus;
  payment_id: number | null;
}

/**
 * Represents the set of field filters available for the /payments/ API
 * collection.
 */
export interface PaymentFilters {
  // General-purpose text search.
  search?: string;
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  payment_method_in?: string;
  amount_min?: number;
  amount_max?: number;
  status_in?: string;
  discount_association_id?: number;
  location_id?: number;
  does_not_have_linked_customer?: boolean;
}

/**
 * Represents the set of field filters available for the /refunds/ API
 * collection.
 */
export interface RefundLogFilters {
  // General-purpose text search.
  search?: string;
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  payment_method_in?: string;
  amount_min?: number;
  amount_max?: number;
  location_id?: number;
}

/**
 * Represents the set of field filters available for the /discount-usages/ API
 * collection.
 */
export interface DiscountUsageLogFilters {
  // General-purpose text search.
  membership_discount_id?: number;
  entry_pass_discount_id?: number;
  event_discount_id?: number;
  is_complete?: boolean;
}

export function loadPaymentFiltersFromGetParams(activatedRoute: ActivatedRoute): PaymentFilters {
  const filters: PaymentFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'payment_method_in', initialQPs);
  loadFloatFilterFromGetParams(filters, 'amount_min', initialQPs);
  loadFloatFilterFromGetParams(filters, 'amount_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadIntFilterFromGetParams(filters, 'discount_association_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'does_not_have_linked_customer', initialQPs);

  return filters;
}

export function loadRefundLogFiltersFromGetParams(activatedRoute: ActivatedRoute): RefundLogFilters {
  const filters: RefundLogFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'payment_method_in', initialQPs);
  loadFloatFilterFromGetParams(filters, 'amount_min', initialQPs);
  loadFloatFilterFromGetParams(filters, 'amount_max', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);

  return filters;
}

export function loadDiscountUsageLogFiltersFromGetParams(activatedRoute: ActivatedRoute): DiscountUsageLogFilters {
  const filters: DiscountUsageLogFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'membership_discount_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'entry_pass_discount_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'event_discount_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_complete', initialQPs);

  return filters;
}

/** The booking a payment paid for, when its invoice is exactly one booking line item; else null. */
export function getSingleBookingId(payment: Payment): number | null {
  const lineItems = payment.line_items;
  if (!lineItems || lineItems.length !== 1) {
    return null;
  }
  const li = lineItems[0];
  if (li.purchase_type !== LineItemPurchaseType.booking || typeof li.booking_id !== 'number') {
    return null;
  }
  return li.booking_id;
}

export function getInvoiceStatusDisplay(invoiceStatus: InvoiceStatus): string {
  switch (invoiceStatus) {
    case InvoiceStatus.paymentPending:
      return 'Payment Pending';
    case InvoiceStatus.paymentComplete:
      return 'Payment Complete';
    case InvoiceStatus.depositPaid:
      return 'Deposit Paid';
    case InvoiceStatus.partiallyRefunded:
      return 'Partially Refunded';
    case InvoiceStatus.refunded:
      return 'Refunded';
    case InvoiceStatus.cancelled:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
}

export function getPaymentStatusDisplay(paymentStatus: PaymentStatus): string {
  switch (paymentStatus) {
    case PaymentStatus.paymentPending:
      return 'Payment Pending';
    case PaymentStatus.paymentProcessing:
      return 'Payment Processing';
    case PaymentStatus.paymentComplete:
      return 'Payment Complete';
    case PaymentStatus.paymentFailed:
      return 'Payment Failed';
    case PaymentStatus.partiallyRefunded:
      return 'Partially Refunded';
    case PaymentStatus.refunded:
      return 'Refunded';
    case PaymentStatus.cancelled:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
}

export function getPaymentTypeDisplay(paymentType: PaymentTypeWithMixed) {
  switch (paymentType) {
    case PaymentType.stripe:
      return 'Stripe (Card or Bank Account)';
    case PaymentType.gravity:
      return 'Gravity (Card or Bank Account)';
    case PaymentType.mxMerchant:
      return 'MX Merchant (Card or Bank Account)';
    case PaymentType.mock:
      return 'Demo';
    case PaymentType.lightspeed:
      return 'Lightspeed POS';
    case PaymentType.square:
      return 'Square POS';
    case PaymentType.credit:
      return 'Account Credit';
    case PaymentType.voucher:
      return 'Voucher';
    case PaymentType.offline:
      return 'Offline Payment';
    case PaymentType.posGiftCard:
      return 'Gift Card';
    case PaymentType.migrated:
      return 'Migrated';
    case PaymentType.na:
      return 'N/A';
    case PaymentTypeMixed.mixed:
      return 'Mixed';
    default:
      return 'Unknown';
  }
}

/**
 * Explain to staff why Capitan cannot refund a membership paid with the given
 * payment type, and where the refund has to be issued instead (PRO-1439).
 *
 * An offline payment gets its own wording: it was taken outside Capitan
 * entirely, so there is no retail platform to send anyone to.
 */
export function getRefundBlockedByPaymentTypeMessage(paymentType: PaymentTypeWithMixed): string {
  const paymentTypeDisplay = getPaymentTypeDisplay(paymentType);

  if (paymentType === PaymentType.offline) {
    return `This membership was paid with an ${paymentTypeDisplay}. `
      + 'Capitan cannot refund that payment. '
      + 'You will need to return the funds outside of Capitan.';
  }

  return `This membership was purchased through ${paymentTypeDisplay}. `
    + 'Capitan cannot refund that payment. '
    + `If you want to issue a refund, you will need to do it directly in ${paymentTypeDisplay}.`;
}

export function getPaymentMethodTypeDisplay(paymentType: PaymentTypeWithMixed | null,
                                            paymentMethodType: PaymentMethodType) {
  switch (paymentType) {
    case null:
      switch (paymentMethodType) {
        case PaymentMethodType.card:
          return 'Credit Card';
        case PaymentMethodType.bankAccount:
          return 'Bank Account';
        case PaymentMethodType.promptPay:
          return 'PromptPay';
        default:
          return 'Unknown';
      }
    case PaymentType.stripe:
      switch (paymentMethodType) {
        case PaymentMethodType.card:
          return 'Credit Card (Stripe)';
        case PaymentMethodType.bankAccount:
          return 'Bank Account (Stripe)';
        case PaymentMethodType.promptPay:
          return 'PromptPay (Stripe)';
        default:
          return 'Stripe'
      }
    case PaymentType.gravity:
      switch (paymentMethodType) {
        case PaymentMethodType.card:
          return 'Credit Card (Gravity)';
        case PaymentMethodType.bankAccount:
          return 'Bank Account (Gravity)';
        case PaymentMethodType.promptPay:
          return 'PromptPay (Gravity)';
        default:
          return 'Gravity'
      }
    case PaymentType.mxMerchant:
      switch (paymentMethodType) {
        case PaymentMethodType.card:
          return 'Credit Card (MX Merchant)';
        case PaymentMethodType.bankAccount:
          return 'Bank Account (MX Merchant)';
        case PaymentMethodType.promptPay:
          return 'PromptPay (MX Merchant)';
        default:
          return 'MX Merchant'
      }
    case PaymentType.mock:
      switch (paymentMethodType) {
        case PaymentMethodType.card:
          return 'Credit Card (Demo)';
        case PaymentMethodType.bankAccount:
          return 'Bank Account (Demo)';
        case PaymentMethodType.promptPay:
          return 'PromptPay (Demo)';
        default:
          return 'Demo'
      }
    case PaymentType.lightspeed:
      return 'Lightspeed POS';
    case PaymentType.square:
      return 'Square POS';
    case PaymentType.credit:
      return 'Account Credit';
    case PaymentType.voucher:
      return 'Voucher';
    case PaymentType.offline:
      return 'Offline Payment';
    case PaymentType.posGiftCard:
      return 'Gift Card';
    case PaymentType.migrated:
      return 'Migrated';
    case PaymentType.na:
      return 'N/A';
    case PaymentTypeMixed.mixed:
      return 'Mixed';
    default:
      return 'Unknown';
  }
}

export function isPOSPayment(payment: {payment_type: PaymentType}): boolean {
  return [
    PaymentType.square,
    PaymentType.lightspeed
  ].includes(payment.payment_type);
}

export function canRefundPayment(payment: {payment_type: PaymentType; status: PaymentStatus}): boolean {
  return (
    [
      PaymentType.gravity,
      PaymentType.stripe,
      PaymentType.mxMerchant,
      PaymentType.mock
    ].includes(payment.payment_type) &&
    [
      PaymentStatus.paymentComplete,
      PaymentStatus.partiallyRefunded
    ].includes(payment.status)
  );
}

/** Mirrors the server's change-payment-customer refusals (PRO-1571). */
export function canChangePaymentCustomer(payment: {payment_type: PaymentType; status: PaymentStatus}): boolean {
  return (
    [
      PaymentStatus.paymentComplete,
      PaymentStatus.partiallyRefunded,
      PaymentStatus.refunded
    ].includes(payment.status) &&
    ![
      PaymentType.credit,
      PaymentType.voucher,
      PaymentType.lightspeed,
      PaymentType.square,
      PaymentType.posGiftCard
    ].includes(payment.payment_type)
  );
}
